import { useState, useEffect } from "react";
import { useQuery } from 'react-query';
import { Table, Tag, Pagination, Spin } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import api from "../adapter/base";
import TimesAgoUtil from "../FreeTripRequests/utils/timesAgo";

const ReferralList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(11);
  const [referrerPhoneSearch, setReferrerPhoneSearch] = useState('');
  const [buyerPhoneSearch, setBuyerPhoneSearch] = useState('');
  const [isCompletedSearch, setIsCompletedSearch] = useState();
  const [orderStatusSearch, setOrderStatusSearch] = useState('');

  useEffect(() => {
    setCurrentPage(1);
  }, [referrerPhoneSearch, buyerPhoneSearch, isCompletedSearch, orderStatusSearch]);

  const { data, isLoading, isError,isFetching} = useQuery({
    queryKey: ['referrals', currentPage, referrerPhoneSearch, buyerPhoneSearch, isCompletedSearch, orderStatusSearch],
    queryFn: async () => {
      const params = {
        page: currentPage,
        paginate: perPage,
        ...(referrerPhoneSearch && { referrer_phone_number_search: referrerPhoneSearch }),
        ...(buyerPhoneSearch && { buyer_phone_number_search: buyerPhoneSearch }),
        ...(isCompletedSearch !== undefined && { is_completed_search: isCompletedSearch }),
        ...(orderStatusSearch && { order_status_search: orderStatusSearch }),
      };
      const queryString = new URLSearchParams(params).toString();
      const { data } = await api.get(`referrals?${queryString}`);
      return data;
    },
    keepPreviousData: true,
  });

  const columns = [
    {
      title: 'Referrer',
      dataIndex: 'referrer',
      key: 'referrer_phone',
      render: (referrer) => <span className="font-medium text-gray-700">{referrer?.name} (+{referrer?.phone_number})</span>,
    },
    {
      title: 'Buyer',
      dataIndex: 'user',
      key: 'user',
      render: (user) => (
          <span className="font-medium text-gray-600">{user?.name} (+{user?.phone_number})</span>
      )
    },
    {
      title: 'Referrer Wallet Reward',
      dataIndex: 'referrer_wallet_reward_price',
      key: 'reward',
      render: (amount) => (
          <span className="font-medium">{amount?.toLocaleString() || '0'} br.
        </span>
      ),
    },
    {
      title: 'Payment Method',
      key: 'payment_method',
      render: (_, record) => {
        if (!record.order) return 'N/A';
        const method = record.order?.invoice?.payment_method;
        return (
            <Tag color="blue" className="rounded-full">
              {method || 'N/A'}
            </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_completed',
      key: 'status',
      render: (is_completed) => (
          <Tag color={is_completed ? 'green' : 'orange'} className="rounded-full">
            {is_completed ? 'Completed' : 'Pending'}
          </Tag>
      ),
    },
    {
      title: 'When',
      dataIndex: 'created_at',
      key: 'date',
      render: (date) => (
          <span className="text-gray-500">
          {date ? TimesAgoUtil(date) : "not registered"}
        </span>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  if (isError) return <div className="text-red-500 p-4">Error loading referrals</div>;

  return (
      <div className="p-4 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-800">Referral Management</h2>
          </div>

          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Referrer Phone
                </label>
                <div className="relative">
                  <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                      type="text"
                      placeholder="Search by phone..."
                      value={referrerPhoneSearch}
                      onChange={(e) => setReferrerPhoneSearch(e.target.value.replace(/\D/g, ''))}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 placeholder:text-gray-400 placeholder:text-sm transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buyer Phone
                </label>
                <div className="relative">
                  <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                      type="text"
                      placeholder="Search by phone..."
                      value={buyerPhoneSearch}
                      onChange={(e) => setBuyerPhoneSearch(e.target.value.replace(/\D/g, ''))}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 placeholder:text-gray-400 placeholder:text-sm focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Referral Status
                </label>
                <div className="relative">
                  <FilterOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                      value={isCompletedSearch ?? ''}
                      onChange={(e) => setIsCompletedSearch(e.target.value ? parseInt(e.target.value) : undefined)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 appearance-none bg-white"
                  >
                    <option value="">All Statuses</option>
                    <option value="1">Completed</option>
                    <option value="0">Pending</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order Status
                </label>
                <div className="relative">
                  <FilterOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <select
                      value={orderStatusSearch}
                      onChange={(e) => setOrderStatusSearch(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 appearance-none bg-white"
                  >
                    <option value="">All Statuses</option>
                    <option value="pending-pickup">Pending Pickup</option>
                    <option value="pending-delivery">Pending Delivery</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="successful">Successfully Delivered</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            <Spin spinning={isFetching} tip="Loading..." className="!text-black custom-yellow-spin"
            >
              <Table
                  columns={columns}
                  dataSource={data?.data || []}
                  rowKey="id"
                  pagination={false}
                  className="rounded-lg"
                  rowClassName="hover:bg-gray-50 transition-colors"
                  locale={{
                    emptyText: isLoading ? ' ' : 'No data found'
                  }}
              />

              <div className="flex justify-center pt-6">
                {data?.total > perPage && <Pagination
                    current={data?.current_page || 1}
                    total={data?.total || 0}
                    pageSize={perPage}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    itemRender={(current, type, originalElement) => {
                      if (type === 'prev' || type === 'next') {
                        return (
                            <button className="px-3 py-1 border rounded-lg hover:bg-gray-100">
                              {type === 'prev' ? 'Previous' : 'Next'}
                            </button>
                        );
                      }
                      return originalElement;
                    }}
                    className="[&_.ant-pagination-item]:rounded-lg [&_.ant-pagination-item]:border-gray-200
                  [&_.ant-pagination-item-active]:border-yellow-500 [&_.ant-pagination-item-active]:bg-blue-50
                  [&_.ant-pagination-item-active]:text-yellow-600 hover:[&_.ant-pagination-item]:bg-gray-100"
                />}
              </div>
            </Spin>
          </div>
        </div>
      </div>
  );
};

export default ReferralList;